import React, { FC, useEffect, useState, useMemo, useCallback } from "react";
import { Product } from "data/data";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NcImage from "shared/NcImage/NcImage";
import Cookies from "js-cookie";
import { useOnlineOrderSettings } from "context/OnlineOrderSettingsContext";
import { useCartDataProvider } from "context/CartProductContext";
import { validateModifiers, getModifierPrice } from "utils/modifierHelpers";
import { getChildModifierSets } from "services/HomeService";
import ModifierSection from "components/ProductCustomization/ModifierSection";
import NestedModifierSection, {
  ChildBundle,
  ChildModItem,
  ChildModSet,
} from "components/ProductCustomization/NestedModifierSection";
import { MinusIcon, PlusIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { ShoppingBagIcon } from "@heroicons/react/24/solid";
import { ProductCardSkeleton } from "shared/Skeleton/Skeleton";
import toast from "react-hot-toast";
import { Transition } from "@headlessui/react";

export interface ProductDetailPageProps {
  className?: string;
}

// --- Nested-modifier bundle helpers (mirrors ProductCustomizationSheet).
// Path is a chain of parent modifier-item ids. Each ChildBundle at a path holds
// the fetched child sets, the user's selections in those sets, and recursive
// bundles for any selected item that itself has grand-children.
const EMPTY_BUNDLE: ChildBundle = {
  fetched: null,
  loading: false,
  selections: {},
  children: {},
};

function getBundleAt(
  root: Record<number, ChildBundle>,
  path: number[]
): ChildBundle | undefined {
  if (path.length === 0) return undefined;
  let cur: ChildBundle | undefined = root[path[0]];
  for (let i = 1; i < path.length && cur; i++) cur = cur.children[path[i]];
  return cur;
}

function updateBundleAt(
  root: Record<number, ChildBundle>,
  path: number[],
  updater: (b: ChildBundle) => ChildBundle
): Record<number, ChildBundle> {
  if (path.length === 0) return root;
  const [head, ...tail] = path;
  const existing = root[head] ?? EMPTY_BUNDLE;
  if (tail.length === 0) return { ...root, [head]: updater(existing) };
  const newChildren = updateBundleAt(existing.children, tail, updater);
  return { ...root, [head]: { ...existing, children: newChildren } };
}

function removeBundleAt(
  root: Record<number, ChildBundle>,
  path: number[]
): Record<number, ChildBundle> {
  if (path.length === 0) return root;
  const [head, ...tail] = path;
  if (!(head in root)) return root;
  if (tail.length === 0) {
    const next = { ...root };
    delete next[head];
    return next;
  }
  const existing = root[head];
  if (!existing) return root;
  return {
    ...root,
    [head]: { ...existing, children: removeBundleAt(existing.children, tail) },
  };
}

function sumBundlePrice(bundle: ChildBundle | undefined): number {
  if (!bundle || !bundle.fetched) return 0;
  let total = 0;
  for (const set of bundle.fetched) {
    const sel = bundle.selections[set.id] ?? {};
    for (const item of set.items) {
      if (sel[item.id]) total += item.onlinePrice || 0;
    }
  }
  for (const child of Object.values(bundle.children)) {
    total += sumBundlePrice(child);
  }
  return total;
}

function validateBundle(bundle: ChildBundle | undefined): boolean {
  if (!bundle || !bundle.fetched) return true;
  for (const set of bundle.fetched) {
    const sel = bundle.selections[set.id] ?? {};
    const selectedCount = Object.values(sel).filter(Boolean).length;
    if (set.noOfRequired > 0 && selectedCount < set.noOfRequired) return false;
    if (set.maximum > 0 && selectedCount > set.maximum) return false;
  }
  for (const child of Object.values(bundle.children)) {
    if (!validateBundle(child)) return false;
  }
  return true;
}

const ProductDetailPage: FC<ProductDetailPageProps> = ({ className = "" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { dispatch } = useCartDataProvider();
  const { items, isLoading } = useOnlineOrderSettings();
  const paramId: any = useParams();
  const id = paramId.id ? parseInt(paramId.id) : undefined;
  const merchantname = Cookies.get("merchantname");

  const [modifierState, setModifierState] = useState<
    Record<number, Record<number, boolean>>
  >(location.state?.modifierChecked || {});
  const [nestedState, setNestedState] = useState<Record<number, ChildBundle>>(
    location.state?.nestedModifierSelections || {}
  );
  const [quantity, setQuantity] = useState(location.state?.quantity || 1);
  const [note, setNote] = useState(location.state?.note || "");
  const [showError, setShowError] = useState(false);

  const isEditMode = !!location.state?.cartItemId;

  // Find the product from context data
  const product = useMemo(() => {
    if (!items || !id) return null;
    for (const category of items as any[]) {
      const found = category.items?.find((item: Product) => item.id === id);
      if (found) return found as Product;
    }
    return null;
  }, [items, id]);

  // Initialize modifier state from edit mode
  useEffect(() => {
    if (location.state?.modifierChecked) {
      setModifierState(location.state.modifierChecked);
    }
  }, [location.state]);

  const fetchAndStoreChildren = useCallback(
    async (path: number[], modifierItemId: number) => {
      setNestedState((prev) =>
        updateBundleAt(prev, path, (b) => ({ ...b, loading: true, error: undefined }))
      );
      try {
        const res = await getChildModifierSets<ChildModSet[]>(modifierItemId);
        const sets = (Array.isArray(res?.data) ? res.data : []) as ChildModSet[];
        setNestedState((prev) =>
          updateBundleAt(prev, path, (b) => ({
            ...b,
            fetched: sets,
            loading: false,
            error: undefined,
          }))
        );
      } catch (err: any) {
        setNestedState((prev) =>
          updateBundleAt(prev, path, (b) => ({
            ...b,
            loading: false,
            error: String(err?.message ?? err),
          }))
        );
      }
    },
    []
  );

  const handleModifierChange = useCallback(
    (modifierSetId: number, linkItemId: number, checked: boolean) => {
      setModifierState((prev) => {
        const updated = { ...prev };
        if (!updated[modifierSetId]) updated[modifierSetId] = {};
        if (checked) {
          updated[modifierSetId] = { ...updated[modifierSetId], [linkItemId]: true };
        } else {
          const setCopy = { ...updated[modifierSetId] };
          delete setCopy[linkItemId];
          updated[modifierSetId] = setCopy;
        }
        return updated;
      });
      setShowError(false);

      if (!product) return;
      const setObj = product.modifierSets?.find((s: any) => s.id === modifierSetId);
      const link = setObj?.modifierSetModifierItems?.find(
        (l: any) => l.id === linkItemId
      );
      const modItem: any = link?.modifierItem;
      if (!modItem) return;
      const modItemId: number = modItem.id;

      if (checked) {
        if (modItem.hasChildModifierSets && !nestedState[modItemId]?.fetched) {
          fetchAndStoreChildren([modItemId], modItemId);
        }
      } else {
        setNestedState((prev) => removeBundleAt(prev, [modItemId]));
      }
    },
    [product, nestedState, fetchAndStoreChildren]
  );

  const handleNestedToggle = useCallback(
    (path: number[], set: ChildModSet, item: ChildModItem, checked: boolean) => {
      setShowError(false);
      const isSingleSelect = set.maximum === 1;

      setNestedState((prev) =>
        updateBundleAt(prev, path, (b) => {
          const newSelections = { ...b.selections };
          const prevSetSelections = newSelections[set.id] ?? {};
          let nextSetSelections: Record<number, boolean>;
          if (checked) {
            nextSetSelections = isSingleSelect
              ? { [item.id]: true }
              : { ...prevSetSelections, [item.id]: true };
          } else {
            nextSetSelections = { ...prevSetSelections };
            delete nextSetSelections[item.id];
          }
          newSelections[set.id] = nextSetSelections;

          const previouslySelectedIds = Object.keys(prevSetSelections)
            .filter((k) => prevSetSelections[Number(k)])
            .map(Number);
          const nextSelectedIds = Object.keys(nextSetSelections)
            .filter((k) => nextSetSelections[Number(k)])
            .map(Number);
          const deselected = previouslySelectedIds.filter(
            (id) => !nextSelectedIds.includes(id)
          );
          let newChildren = b.children;
          for (const id of deselected) {
            if (id in newChildren) {
              const next = { ...newChildren };
              delete next[id];
              newChildren = next;
            }
          }
          return { ...b, selections: newSelections, children: newChildren };
        })
      );

      if (checked && item.hasChildModifierSets) {
        const childPath = [...path, item.id];
        if (!getBundleAt(nestedState, childPath)?.fetched) {
          fetchAndStoreChildren(childPath, item.id);
        }
      }
    },
    [nestedState, fetchAndStoreChildren]
  );

  const renderBundle = useCallback(
    (path: number[], depth: number): React.ReactNode => {
      const bundle = getBundleAt(nestedState, path);
      if (!bundle) return null;
      if (bundle.loading) {
        return (
          <div className="ml-6 py-2 text-xs text-slate-500 dark:text-slate-400">
            Loading options…
          </div>
        );
      }
      if (bundle.error) {
        return (
          <div className="ml-6 py-2 text-xs text-red-500 dark:text-red-400">
            {bundle.error}
          </div>
        );
      }
      if (!bundle.fetched || bundle.fetched.length === 0) return null;
      const sorted = [...bundle.fetched].sort(
        (a, b) => (a.orderBy ?? 0) - (b.orderBy ?? 0)
      );
      return (
        <div className="pl-4">
          {sorted.map((set) => (
            <NestedModifierSection
              key={set.id}
              set={set}
              selectedInSet={bundle.selections[set.id] ?? {}}
              onToggle={(item, checked) =>
                handleNestedToggle(path, set, item, checked)
              }
              renderChildrenAfter={(itemId) => renderBundle([...path, itemId], depth + 1)}
              showError={showError}
              depth={depth}
            />
          ))}
        </div>
      );
    },
    [nestedState, handleNestedToggle, showError]
  );

  const runningTotal = useMemo(() => {
    if (!product) return 0;
    const modPrice = getModifierPrice(product, modifierState);
    const nestedPrice = Object.values(nestedState).reduce(
      (acc, b) => acc + sumBundlePrice(b),
      0
    );
    return (product.onlinePrice + modPrice + nestedPrice) * quantity;
  }, [product, modifierState, nestedState, quantity]);

  const isValid = useMemo(() => {
    if (!product) return false;
    const top = validateModifiers(product, modifierState);
    if (!top.valid) return false;
    for (const b of Object.values(nestedState)) {
      if (!validateBundle(b)) return false;
    }
    return true;
  }, [product, modifierState, nestedState]);

  const handleAddToCart = () => {
    if (!product) return;

    if (!isValid) {
      setShowError(true);
      return;
    }

    const cartProduct: any = {
      ...product,
      quantity,
      modifierChecked: modifierState,
      nestedModifierSelections: nestedState,
      note,
    };

    if (isEditMode) {
      dispatch({
        type: "EDIT_CART",
        payload: { ...cartProduct, cartItemId: location.state.cartItemId },
      });
    } else {
      dispatch({ type: "ADD_TO_CART", payload: cartProduct });
    }

    toast.custom(
      (t) => (
        <Transition
          appear
          show={t.visible}
          className="p-3 max-w-sm w-full bg-white dark:bg-slate-800 shadow-lg rounded-xl pointer-events-auto ring-1 ring-black/5 dark:ring-white/10 text-slate-900 dark:text-slate-100"
          enter="transition-all duration-200"
          enterFrom="opacity-0 translate-y-4"
          enterTo="opacity-100 translate-y-0"
          leave="transition-all duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100 dark:bg-slate-700">
              {product.imageUrl && (
                <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{product.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {isEditMode ? "Updated in cart" : "Added to cart"}
              </p>
            </div>
          </div>
        </Transition>
      ),
      { position: "bottom-center", id: `detail-${product.id}`, duration: 1500 }
    );

    navigate(`/${merchantname}`);
  };

  // Loading state
  if (isLoading || !items) {
    return (
      <div className="container py-10">
        <div className="max-w-2xl mx-auto">
          <ProductCardSkeleton className="w-full" />
        </div>
      </div>
    );
  }

  // Product not found
  if (!product) {
    return (
      <div className="container py-16 text-center">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">
          Item not found
        </h2>
        <p className="text-slate-500 dark:text-slate-400 mb-6">
          This menu item may no longer be available.
        </p>
        <button
          onClick={() => navigate(`/${merchantname}`)}
          className="px-6 py-2.5 bg-primary-500 dark:bg-primary-400 text-white dark:text-slate-900 rounded-full font-semibold text-sm"
        >
          Back to Menu
        </button>
      </div>
    );
  }

  return (
    <div className={`nc-ProductDetailPage bg-white dark:bg-slate-900 min-h-[60vh] ${className}`}>
      {/* Back button */}
      <div className="container pt-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back
        </button>
      </div>

      <main className="container pb-24 pt-4">
        <div className="max-w-3xl mx-auto lg:flex lg:max-w-5xl lg:gap-10">
          {/* Image */}
          <div className="lg:w-[45%]">
            <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-stone-100 dark:bg-slate-800">
              <NcImage
                containerClassName="w-full h-full"
                src={product.imageUrl || ""}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div className="lg:w-[55%] mt-6 lg:mt-0">
            {/* Name & price */}
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">
              {product.name}
            </h1>
            <p className="text-xl font-bold text-primary-600 dark:text-primary-400 mt-2">
              ${product.onlinePrice.toFixed(2)}
              {product.unit?.name && (
                <span className="text-sm font-normal text-slate-400 ml-1">
                  / {product.unit.name}
                </span>
              )}
            </p>
            {product.description && (
              <p className="text-slate-500 dark:text-slate-400 mt-3">
                {product.description}
              </p>
            )}

            {/* Modifier sections */}
            {product.modifierSets && product.modifierSets.length > 0 && (
              <div className="mt-6 divide-y divide-slate-100 dark:divide-slate-700/50 border-t border-slate-100 dark:border-slate-700/50">
                {product.modifierSets.map((modSet) => (
                  <ModifierSection
                    key={modSet.id}
                    modifierSet={modSet}
                    selectedState={modifierState[modSet.id] || {}}
                    onChange={handleModifierChange}
                    showError={showError}
                    renderNestedAfter={(modifierItemId) => renderBundle([modifierItemId], 1)}
                  />
                ))}
              </div>
            )}

            {/* Note */}
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Special Instructions
              </h4>
              <textarea
                placeholder="Any special requests?"
                className="w-full h-16 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent resize-none"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Sticky footer */}
      <div
        className="fixed bottom-0 inset-x-0 z-50 px-4 py-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.3)]"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
      >
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          {/* Quantity */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-full px-1 py-1">
            <button
              onClick={() => setQuantity((q: number) => Math.max(1, q - 1))}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors"
              disabled={quantity <= 1}
            >
              <MinusIcon className="w-5 h-5" />
            </button>
            <span className="w-8 text-center font-bold text-slate-900 dark:text-slate-100">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity((q: number) => q + 1)}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Add to cart */}
          <button
            onClick={handleAddToCart}
            disabled={!product.isAvailable || !isValid}
            className={`flex-1 h-12 flex items-center justify-center gap-2 rounded-xl font-bold text-base shadow-md active:scale-[0.98] transition-all ${
              product.isAvailable && isValid
                ? "bg-accent-500 dark:bg-accent-400 text-white dark:text-slate-900 hover:bg-accent-600 dark:hover:bg-accent-500"
                : "bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed"
            }`}
          >
            <ShoppingBagIcon className="w-5 h-5" />
            <span>
              {isEditMode ? "Update" : "Add to Cart"} - ${runningTotal.toFixed(2)}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
